import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Spinner } from '@fluentui/react-components';
import { MsalProvider, useIsAuthenticated, useMsal } from '@azure/msal-react';
import {
  EventType,
  InteractionStatus,
  PublicClientApplication,
  type AccountInfo,
  type AuthenticationResult,
  type EventMessage,
} from '@azure/msal-browser';
import { jwtDecode, type JwtPayload } from 'jwt-decode';
import { isRoleKey, type MountUser, type RoleKey } from '@unified-governance/shared';
import { environment } from '../environment';
import { loginRequest, msalConfig } from './msalConfig';
import { AccessDenied } from '../components/AccessDenied/AccessDenied';
import { ShellAuthContext } from './ShellAuthContext';

interface DecodedToken extends JwtPayload {
  roles?: string[];
}

interface AccountClaims {
  oid?: string;
  roles?: string[];
}

let msalInstance: PublicClientApplication | null = null;

function toMountUser(account: AccountInfo): MountUser {
  const claims = account.idTokenClaims as AccountClaims | undefined;
  return {
    homeAccountId: account.homeAccountId,
    localAccountId: account.localAccountId,
    tenantId: account.tenantId,
    username: account.username,
    name: account.name,
    objectId: claims?.oid,
  };
}

async function getOrCreateMsalInstance(): Promise<PublicClientApplication> {
  if (!msalInstance) {
    const instance = new PublicClientApplication(msalConfig);
    await instance.initialize();

    instance.addEventCallback((event: EventMessage) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = event.payload as AuthenticationResult;
        instance.setActiveAccount(payload.account);
      }
    });

    const response = await instance.handleRedirectPromise();
    if (response?.account) {
      instance.setActiveAccount(response.account);
    } else {
      const accounts = instance.getAllAccounts();
      if (accounts.length > 0) {
        instance.setActiveAccount(accounts[0]);
      }
    }

    msalInstance = instance;
  }

  return msalInstance;
}

function ShellAuthGuard({ children }: { children: ReactNode }) {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [roles, setRoles] = useState<RoleKey[]>([]);

  const getAccessToken = useCallback(
    async (scope: string): Promise<string> => {
      const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
      if (!account) {
        throw new Error('No authenticated account available');
      }
      try {
        const response = await instance.acquireTokenSilent({ scopes: [scope], account });
        return response.accessToken;
      } catch {
        await instance.acquireTokenRedirect({ scopes: [scope], account });
        return '';
      }
    },
    [instance]
  );

  useEffect(() => {
    if (!isAuthenticated && inProgress === InteractionStatus.None) {
      instance.loginRedirect(loginRequest).catch(() => undefined);
    }
  }, [isAuthenticated, inProgress, instance]);

  useEffect(() => {
    if (!isAuthenticated || inProgress !== InteractionStatus.None) {
      return;
    }
    let active = true;
    getAccessToken(environment.tokenScope)
      .then((token) => {
        if (!token || !active) {
          return;
        }
        const decoded = jwtDecode<DecodedToken>(token);
        const next = (decoded.roles ?? []).filter(isRoleKey);
        if (active) {
          setRoles(next);
        }
      })
      .catch(() => {
        if (active) {
          setRoles([]);
        }
      });
    return () => {
      active = false;
    };
  }, [isAuthenticated, inProgress, getAccessToken]);

  const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
  const user = useMemo(() => (account ? toMountUser(account) : null), [account]);
  const hasRequiredAppRole = useMemo(() => {
    if (!account) {
      return false;
    }

    const claims = account.idTokenClaims as AccountClaims | undefined;
    return (claims?.roles ?? []).includes(environment.requiredAppRole);
  }, [account]);
  const value = useMemo(
    () => (user ? { roles, user, getAccessToken } : null),
    [roles, user, getAccessToken]
  );

  if (!isAuthenticated || inProgress !== InteractionStatus.None || !value) {
    return <Spinner className="shell-loading-spinner" label="Logging in. Please wait..." />;
  }

  if (!hasRequiredAppRole || false) {
    return <AccessDenied />;
  }

  return <ShellAuthContext.Provider value={value}>{children}</ShellAuthContext.Provider>;
}

export function MsalShellAuthProvider({ children }: { children: ReactNode }) {
  const [instance, setInstance] = useState<PublicClientApplication | null>(null);

  useEffect(() => {
    getOrCreateMsalInstance().then(setInstance);
  }, []);

  if (!instance) {
    return <Spinner className="shell-loading-spinner" label="Logging in. Please wait..." />;
  }

  return (
    <MsalProvider instance={instance}>
      <ShellAuthGuard>{children}</ShellAuthGuard>
    </MsalProvider>
  );
}
